// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../validation/runtime-checks/index.js';
import Latex from '../Latex.js';
import Image from '../Image.js';
import { CartesianPlane } from '../CartesianPlane.js';
import DraftImage from '../DraftImage.js';
import { LatexTextAtom } from '../LatexTextAtom.js';

const cellStyle = {
  display: 'table-cell',
  verticalAlign: 'top',
  textAlign: 'center',
  width: '50%',
  breakInside: 'avoid',
};

export function SolidifyFigureGrid(props: {|
  children: ChildrenArray<Element<typeof SolidifyFigureGridItem>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    SolidifyFigureGrid,
  );

  React.useEffect(() => {
    if (!props._performValidation || failedValidation) return;

    const cs = React.Children.toArray(props.children);
    const getFigure = (gridItem) => gridItem.props.children[0];
    // NOTE that we rely on the fact that the validation of the child
    // will have occurred before we run this so we know the 0th child
    // of SolidifyFigureGridItem must exist and be one of the figure element
    // types.
    const figure = getFigure(cs[0]);

    if (cs.length < 2) {
      addErrors([
        {
          message: `The grid must have at least 2 items`,
          ...locations.children,
        },
      ]);
    }

    if (cs.some((c) => getFigure(c).type !== figure.type)) {
      addErrors([
        {
          message: `All figure components in the grid must be the same type`,
          ...locations.children,
        },
      ]);
    }

    if (
      cs.some(
        (c) =>
          // $FlowFixMe withValidation HOC is messing up props for CartesianPlane
          getFigure(c).props.width !== figure.props.width ||
          // $FlowFixMe withValidation HOC is messing up props for CartesianPlane
          getFigure(c).props.height !== figure.props.height,
      )
    ) {
      addErrors([
        {
          message: `All figures in the grid must be the same size`,
          ...locations.children,
        },
      ]);
    }
  });

  // For now we will enforce a 2 column grid. It is not clear at this time
  // whether more columns can be used sensibly, or are a necessity.
  const cs = React.Children.toArray(props.children);
  const rows = groupsOf(2, cs);
  // We need to pad out the final row with empty cells to ensure cells
  // do not expand to fill the space. It may make sense to prevent
  // unbalanced grids at the validation layer for aesthetic reasons, in which
  // case this becomes unecessary.
  if (cs.length % 2 !== 0) {
    rows[rows.length - 1].push(<div style={cellStyle} />);
  }

  if (failedValidation) return null;

  return (
    <div style={{ margin: '32px 0' }}>
      {rows.map((r, i) => (
        <React.Fragment key={i}>
          <div
            style={{
              display: 'table',
              tableLayout: 'fixed',
              width: '100%',
              breakInside: 'avoid',
            }}
            key={i}
          >
            {r}
          </div>
          {i !== rows.length - 1 && <div style={{ height: 16 }} />}
        </React.Fragment>
      ))}
    </div>
  );
}
SolidifyFigureGrid.spec = {
  children: t.childrenArray(t.elementType(SolidifyFigureGridItem)),
};

export function SolidifyFigureGridItem(props: {|
  children: [
    Element<typeof Image> | Element<typeof CartesianPlane>,
    Element<typeof SolidifyFigureGridCaption>,
  ],
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors } = usePropsValidation(
    props,
    SolidifyFigureGridItem,
  );

  const [figure, caption] = React.Children.toArray(props.children);
  React.useEffect(() => {
    if (!props._performValidation || failedValidation) return;
    const { width, height, _sourceLocations } = figure.props;
    if (width !== 260) {
      addErrors([
        {
          message:
            'width must be explicitly set to 260. 260x260 is the only allowed size.',
          ...(width !== undefined
            ? _sourceLocations.width
            : _sourceLocations[getComponentName(figure)]),
        },
      ]);
    }
    if (height !== 260) {
      addErrors([
        {
          message:
            'height must be explicitly set to 260. 260x260 is the only allowed size.',
          ...(height !== undefined
            ? _sourceLocations.height
            : _sourceLocations[getComponentName(figure)]),
        },
      ]);
    }
  });

  if (failedValidation) return null;

  return (
    <div style={cellStyle}>
      {React.cloneElement(figure, { _removeFigureMargin: true })}
      {caption}
    </div>
  );
}
SolidifyFigureGridItem.spec = {
  children: t.tuple([
    // 🚨 WARNING 🚨
    // If you wish to allow more figure types you must ensure they handle
    // the _removeFigureMargin behaviour otherwise you'll stuff up the layout.
    t.unionOfTypes(
      t.elementType(Image),
      t.elementType(DraftImage),
      t.elementType(CartesianPlane),
    ),
    t.elementType(SolidifyFigureGridCaption),
  ]),
};

export function SolidifyFigureGridCaption(props: {
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
  ...CompilerInjectedProps,
}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    SolidifyFigureGridCaption,
  );

  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!props._performValidation || failedValidation) return;
    if (ref.current === null) return;
    if (ref.current.getClientRects().length > 2) {
      addErrors([
        {
          message: 'Captions cannot be longer than 2 lines',
          ...locations.children,
        },
      ]);
    }

    const cs = React.Children.toArray(props.children);
    if (
      cs.length === 1 &&
      typeof cs[0] === 'string' &&
      cs[0].trim().length === 0
    ) {
      addErrors([
        {
          message: 'Captions cannot be empty',
          ...locations.children,
        },
      ]);
    }
  });

  if (failedValidation) return null;
  return (
    <>
      {/* Allow enough spacing between the figure and the caption */}
      <div style={{ height: 24 }} />
      {/* display: inline is *cricital* so that .getClientRects() has
          access to the CSS line boxes */}
      <div ref={ref} style={{ display: 'inline' }}>
        {props.children}
      </div>
    </>
  );
}
SolidifyFigureGridCaption.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};

function groupsOf(n, xs) {
  const groups = [];
  for (let i = 0; i < xs.length; i++) {
    if (i % n === 0) groups.push([]);
    groups[groups.length - 1].push(xs[i]);
  }
  return groups;
}

function getComponentName(element) {
  return element.type.__wrappedComponent
    ? element.type.__wrappedComponent.name
    : element.type.name;
}
